import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || '';
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

let initialized = false;

/**
 * Initialize PostHog with full session recording, autocapture,
 * and feature flag support for A/B testing.
 */
export function initAnalytics() {
  if (initialized || !POSTHOG_KEY) return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: { password: true },
    },
    // Load feature flags on init for A/B testing
    loaded: (ph) => {
      // Reload feature flags to ensure fresh state
      ph.reloadFeatureFlags();
    },
    // Enable toolbar for visual editing
    advanced_disable_toolbar_metrics: false,
  });

  initialized = true;
}

// ─── Core Tracking ───

export function trackEvent(event: string, properties?: Record<string, unknown>) {
  if (!initialized && POSTHOG_KEY) initAnalytics();
  if (!POSTHOG_KEY) return;
  posthog.capture(event, properties);
}

export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return;
  posthog.identify(userId, traits);
}

// ─── Feature Flags & A/B Testing ───

export function getFeatureFlag(flag: string): string | boolean | undefined {
  if (!POSTHOG_KEY) return undefined;
  return posthog.getFeatureFlag(flag);
}

/**
 * Determine which variant (A or B) to show for a given audience page.
 * Uses PostHog feature flags: `sales-page-{slug}`
 * Falls back to 'A' if PostHog is not loaded or flag is not set.
 */
export function getVariant(audienceSlug: string): 'A' | 'B' {
  const flag = `sales-page-${audienceSlug}`;
  const variant = getFeatureFlag(flag);

  // PostHog returns 'control' or 'test' for experiments,
  // or true/false for simple flags, or string variants
  if (variant === 'B' || variant === 'test' || variant === true) return 'B';
  return 'A';
}

/**
 * Register the user in a PostHog experiment for a given audience page.
 * This ensures the experiment exposure is tracked.
 */
export function registerExperimentExposure(audienceSlug: string, variant: 'A' | 'B') {
  trackEvent('$experiment_exposure', {
    $experiment_name: `sales-page-${audienceSlug}`,
    $variant: variant === 'A' ? 'control' : 'test',
    audience: audienceSlug,
  });
}

// ─── Page-Level Tracking ───

export function trackPageView(page: string, variant?: 'A' | 'B', audience?: string) {
  trackEvent('sales_page_view', {
    page,
    variant,
    audience,
    referrer: document.referrer || 'direct',
    url: window.location.href,
    timestamp: new Date().toISOString(),
  });
}

export function trackCTAClick(ctaId: string, location?: string, variant?: 'A' | 'B', audience?: string) {
  trackEvent('cta_click', {
    cta_id: ctaId,
    location,
    variant,
    audience,
    url: window.location.href,
  });
}

export function trackSectionView(section: string, variant?: 'A' | 'B', audience?: string) {
  trackEvent('section_view', { section, variant, audience });
}

// ─── Conversion Funnel Tracking ───

export function trackSignupStart(audience: string, variant: 'A' | 'B') {
  trackEvent('signup_start', { audience, variant });
}

export function trackSignupComplete(audience: string, variant: 'A' | 'B') {
  trackEvent('signup_complete', { audience, variant });
}

export function trackFormInteraction(audience: string, variant: 'A' | 'B', field: string) {
  trackEvent('form_field_interaction', { audience, variant, field });
}

// ─── Engagement Tracking ───

export function trackScrollDepth(depth: number, audience: string, variant: 'A' | 'B') {
  trackEvent('scroll_depth', { depth_percent: depth, audience, variant });
}

export function trackTimeOnPage(seconds: number, audience: string, variant: 'A' | 'B') {
  trackEvent('time_on_page', { seconds, audience, variant });
}

export function trackFAQOpen(question: string, audience: string, variant: 'A' | 'B') {
  trackEvent('faq_opened', { question, audience, variant });
}

export function trackTestimonialView(testimonialIndex: number, audience: string, variant: 'A' | 'B') {
  trackEvent('testimonial_viewed', { testimonial_index: testimonialIndex, audience, variant });
}

// ─── Scroll Depth Observer ───

let scrollObserverActive = false;
const scrollMilestones = new Set<number>();

/**
 * Start tracking scroll depth milestones (25%, 50%, 75%, 90%, 100%).
 * Call once per page load.
 */
export function startScrollDepthTracking(audience: string, variant: 'A' | 'B') {
  if (scrollObserverActive) return;
  scrollObserverActive = true;
  scrollMilestones.clear();

  const milestones = [25, 50, 75, 90, 100];

  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;

    const percent = Math.round((scrollTop / docHeight) * 100);

    for (const milestone of milestones) {
      if (percent >= milestone && !scrollMilestones.has(milestone)) {
        scrollMilestones.add(milestone);
        trackScrollDepth(milestone, audience, variant);
      }
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });

  // Cleanup on navigation
  return () => {
    window.removeEventListener('scroll', handleScroll);
    scrollObserverActive = false;
  };
}

// ─── Time on Page Tracker ───

/**
 * Start tracking time on page. Fires events at 15s, 30s, 60s, 120s, 300s.
 * Returns cleanup function.
 */
export function startTimeOnPageTracking(audience: string, variant: 'A' | 'B') {
  const milestones = [15, 30, 60, 120, 300];
  const fired = new Set<number>();
  const startTime = Date.now();

  const interval = setInterval(() => {
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    for (const ms of milestones) {
      if (elapsed >= ms && !fired.has(ms)) {
        fired.add(ms);
        trackTimeOnPage(ms, audience, variant);
      }
    }
    // Stop checking after all milestones fired
    if (fired.size === milestones.length) {
      clearInterval(interval);
    }
  }, 5000);

  return () => clearInterval(interval);
}

// ─── Section Visibility Observer ───

/**
 * Create an IntersectionObserver that fires section_view events
 * when sections become visible. Returns cleanup function.
 */
export function createSectionObserver(audience: string, variant: 'A' | 'B') {
  const observed = new Set<string>();

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const section = entry.target.getAttribute('data-section');
          if (section && !observed.has(section)) {
            observed.add(section);
            trackSectionView(section, variant, audience);
          }
        }
      }
    },
    { threshold: 0.3 }
  );

  // Observe all elements with data-section attribute
  requestAnimationFrame(() => {
    document.querySelectorAll('[data-section]').forEach((el) => {
      observer.observe(el);
    });
  });

  return () => observer.disconnect();
}

export { posthog };
